import { useState } from "react";
import { View, Text, TouchableOpacity, KeyboardAvoidingView, Platform, ScrollView } from "react-native";
import { Link, useLocalSearchParams } from "expo-router";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";

const API_URL = (process.env.EXPO_PUBLIC_API_URL || "https://dashhound.app") + "/api";

export default function ResetPasswordScreen() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit() {
    if (!password || !confirmPassword) {
      setError("Please fill in both fields");
      return;
    }
    if (password.length < 10) {
      setError("Password must be at least 10 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (!token) {
      setError("Invalid reset link");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Reset failed");
      }
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      style={{ flex: 1, backgroundColor: "#f9fafb" }}
    >
      <ScrollView contentContainerStyle={{ flexGrow: 1, justifyContent: "center", padding: 24 }}>
        <View style={{ marginBottom: 40, alignItems: "center" }}>
          <Text style={{ fontSize: 36, fontWeight: "800", color: "#111827" }}>DashHound</Text>
          <Text style={{ fontSize: 16, color: "#6b7280", marginTop: 8 }}>Choose a new password</Text>
        </View>

        {error ? (
          <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 16 }}>
            <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
          </View>
        ) : null}

        {success ? (
          <View style={{ backgroundColor: "#f0fdf4", padding: 20, borderRadius: 12, alignItems: "center" }}>
            <Text style={{ fontSize: 18, fontWeight: "700", color: "#16a34a", marginBottom: 8 }}>Password reset!</Text>
            <Text style={{ fontSize: 14, color: "#374151", textAlign: "center", lineHeight: 22, marginBottom: 16 }}>
              Your password has been updated. You can now sign in with your new password.
            </Text>
            <Link href="/(auth)/login/" asChild>
              <TouchableOpacity style={{ backgroundColor: "#1B2A4A", paddingHorizontal: 24, paddingVertical: 12, borderRadius: 10 }}>
                <Text style={{ color: "#fff", fontWeight: "700", fontSize: 16 }}>Sign In</Text>
              </TouchableOpacity>
            </Link>
          </View>
        ) : !token ? (
          <View style={{ backgroundColor: "#fef2f2", padding: 20, borderRadius: 12, alignItems: "center" }}>
            <Text style={{ fontSize: 16, fontWeight: "700", color: "#dc2626", marginBottom: 8 }}>Invalid reset link</Text>
            <Text style={{ fontSize: 14, color: "#374151", textAlign: "center" }}>
              This link is invalid or has expired. Please request a new one.
            </Text>
            <Link href="/(auth)/forgot-password/" asChild>
              <TouchableOpacity style={{ marginTop: 16 }}>
                <Text style={{ color: "#2563eb", fontSize: 14, fontWeight: "600" }}>Request new link</Text>
              </TouchableOpacity>
            </Link>
          </View>
        ) : (
          <>
            <Input
              label="New Password"
              value={password}
              onChangeText={setPassword}
              placeholder="At least 10 characters"
              secureTextEntry
            />
            <Input
              label="Confirm Password"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              placeholder="Confirm your new password"
              secureTextEntry
            />
            <Button title="Reset Password" onPress={handleSubmit} loading={loading} />
          </>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}
